import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";

import {
  MeasurementRequiredData_Input,
  PatientMonitoringType,
  PermissionBit,
  Sex,
} from "../../api";
import { AppModel } from "../../app/app-model";
import { NameFields } from "../../components/name-fields";
import { HelperTooltip } from "../../components/ui-kit/base/helper-tooltip";
import { Button } from "../../components/ui-kit/button";
import { ChooseField } from "../../components/ui-kit/fields/choose-field";
import { Combobox } from "../../components/ui-kit/fields/combobox";
import { DateInput } from "../../components/ui-kit/fields/date-input";
import { Radio } from "../../components/ui-kit/fields/radio";
import { Select } from "../../components/ui-kit/fields/select";
import { Switch } from "../../components/ui-kit/fields/switch";
import { TextInput } from "../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import { Form } from "../../components/ui-kit/form/form";
import { FormField } from "../../components/ui-kit/form/form-field";
import { FormGrid } from "../../components/ui-kit/form/form-grid";
import { RequiredAsterisk } from "../../components/ui-kit/required-asterisk";
import { Spinner } from "../../components/ui-kit/spinner";
import { SimpleTable } from "../../components/ui-kit/tables/simple-table";
import { deviceHandovers, tokenExpiries } from "../../constants/constants";
import {
  monitoringTypes,
  raceOptions,
  supportedLanguages,
} from "../../constants/enum-to-text";
import {
  hasAdminAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../../constants/permissions";
import { PhysiciansModal } from "../admin/common/physicians-modal";
import { UsersModal } from "../admin/common/users-modal";
import { loadPhysicians } from "../admin/physicians/physicians";
import { ClinicalStudyEnrollmentFormFields } from "../clinical-studies/enrollment-form-fields";
import { CountrySelect } from "../patient/country-select";
import { RegionSelect } from "../patient/region-select";
import { AddingDialog, dataTypes } from "../patient/self-measurement-config";
import { AddPatient } from "./add-patient";

export const AddPatientRoute = observer(function AddPatientRoute({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const [model, setModel] = useState<AddPatient | null>(null);
  const [addingMeasurement, setAddingMeasurement] = useState(false);
  useEffect(() => {
    const init = async () => {
      const m = await app.initAddPatient();
      setModel(m);
    };
    void init();
  }, [app]);

  useEffect(() => {
    if (!model) return;
    if (model.enrollInStudy) {
      void model.tryFetchPrefill();
    } else {
      model.resetEnrollment();
    }
  }, [
    model,
    model?.enrollInStudy,
    model?.addingList.country,
    model?.enrollment.study_acronym,
    model?.enrollment.enrollment_date,
  ]);

  const PhysiciansModalComponent = useCallback(
    ({ onClose }: { onClose: () => void }) => {
      if (!model) {
        return null;
      }
      const physiciansModel = model.createPhysicians();
      return (
        <PhysiciansModal
          title={<Trans>Choose physicians for a new patient</Trans>}
          preselected={model.addingList["append_to_physicians"]}
          filter={{ country: [model.addingList.country] }}
          mode="multi"
          model={physiciansModel}
          onClose={onClose}
          onConfirm={({ selected }) => {
            model.addingList["append_to_physicians"] = selected;
          }}
          allowCreate
          app={app}
          onAfterCreate={() => loadPhysicians(physiciansModel)}
        />
      );
    },
    [model, app],
  );

  const ManagingUsersModal = useCallback(
    ({ onClose }: { onClose: () => void }) => {
      if (!model) {
        return null;
      }
      const usersModel = model.createUsers();
      return (
        <UsersModal
          title={<Trans>Choose managing users for a new patient</Trans>}
          preselected={model.addingList["append_to_managing_users"]}
          mode="multi"
          model={usersModel}
          onClose={onClose}
          onConfirm={({ selected }) => {
            model.addingList["append_to_managing_users"] = selected;
          }}
        />
      );
    },
    [model],
  );

  if (!model) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  return (
    <>
      <div className="mb-2 text-xs">
        <RequiredAsterisk /> <Trans>= mandatory</Trans>
      </div>
      <Form
        onSubmit={() => {
          model.persistRememberedFields();
          return model.post();
        }}
      >
        <div className="flex max-w-xl flex-col gap-4">
          <FormGrid>
            {!model.simplified && (
              <FormField
                label={<Trans>Monitoring type</Trans>}
                tooltip={
                  <Trans>
                    Self-monitoring is for patients measuring at home; physician
                    supervision is for research or single measurements only. For
                    self-monitoring patients, ensure a user account is created.
                  </Trans>
                }
                mandatory
              >
                <Select
                  value={model.addingList["monitoring_type"]}
                  onChange={(newValue) => {
                    model.addingList["monitoring_type"] = newValue;
                    if (newValue === PatientMonitoringType.self_measurement) {
                      model.createUser = true;
                    }
                  }}
                  values={monitoringTypes}
                  listTitle={t`Select monitoring`}
                  required
                />
              </FormField>
            )}

            <FormField label={<Trans>Date of birth</Trans>} mandatory>
              <DateInput
                type="date"
                attr="date_of_birth"
                model={model.addingList}
                required
              />
            </FormField>

            <FormField label={<Trans>Sex</Trans>} mandatory>
              <Radio
                attr="sex"
                model={model.addingList}
                values={Sex}
                required
              />
            </FormField>

            <FormField label={<Trans>Race</Trans>} mandatory>
              <Combobox
                attr="race"
                model={model.addingList}
                options={raceOptions}
                required
              />
            </FormField>

            <FormField label={<Trans>Country</Trans>} mandatory>
              <CountrySelect attr="country" model={model.addingList} required />
            </FormField>

            <FormField label={<Trans>Region</Trans>} mandatory>
              <RegionSelect
                attr="region"
                country={model.addingList.country}
                model={model.addingList}
                required
              />
            </FormField>
          </FormGrid>

          <NameFields model={model.addingList} />

          <FormGrid>
            <FormField
              label={<Trans>Email</Trans>}
              tooltip={
                <Trans>
                  For self-monitoring patients, this must be an email the
                  patient can access — they will receive a password setup link.
                </Trans>
              }
              mandatory
            >
              <TextInput attr="email" model={model.addingList} required />
            </FormField>

            {!model.simplified && (
              <FormField label={<Trans>Phone</Trans>}>
                <TextInput attr="phone" model={model.addingList} />
              </FormField>
            )}

            <FormField
              label={<Trans>Health insurance provider</Trans>}
              tooltip={
                <Trans>
                  Although required, you may enter 'N/A' if this information is
                  not needed for billing purposes.
                </Trans>
              }
              mandatory
            >
              <TextInput
                attr="health_insurance"
                model={model.addingList}
                required
              />
            </FormField>

            {!model.simplified && (
              <>
                <FormField label={<Trans>ID number</Trans>}>
                  <TextInput attr="id_number" model={model.addingList} />
                </FormField>

                <FormField label={<Trans>Height (cm)</Trans>}>
                  <TextInput attr="height" model={model.addingList} />
                </FormField>

                <FormField label={<Trans>Residence</Trans>}>
                  <TextInput attr="residence" model={model.addingList} />
                </FormField>

                <FormField label={<Trans>Description</Trans>} span={2}>
                  <TextInput
                    attr="description"
                    model={model.addingList}
                    multiline="autosize"
                    rows={2}
                  />
                </FormField>
              </>
            )}
          </FormGrid>

          <div className="border-border border-t" />

          <div>
            <div className="mb-2 flex items-center gap-1">
              <h3 className="text-sm font-semibold">
                <Trans>Measurement configuration</Trans>
              </h3>
              <HelperTooltip
                content={
                  <Trans>
                    The patient will be automatically assigned the default
                    configuration based on the selected country if left empty.
                    To set a specific configuration, add it here. Any steps
                    added here replace the defaults entirely — include every
                    step the patient needs.
                  </Trans>
                }
              />
            </div>
            {model.addingList.self_measurement_config.length > 0 && (
              <SimpleTable<MeasurementRequiredData_Input>
                rows={model.addingList.self_measurement_config}
                columns={[
                  {
                    header1: "#",
                    cell: (_row, index) => index + 1,
                  },
                  {
                    header1: <Trans>Type</Trans>,
                    cell: (row) => resolveEnumLabel(dataTypes[row.data_type]),
                  },
                  {
                    header1: <Trans>Data name</Trans>,
                    cell: (row) =>
                      ("data_name" in row ? row.data_name : null) ??
                      resolveEnumLabel(dataTypes[row.data_type]),
                  },
                  {
                    header1: "",
                    cell: (_row, index) => (
                      <Button
                        variant="tertiary"
                        size="sm"
                        destructive
                        onClick={() => {
                          model.addingList.self_measurement_config.splice(
                            index,
                            1,
                          );
                        }}
                      >
                        <Trans>Remove</Trans>
                      </Button>
                    ),
                  },
                ]}
              />
            )}
            <Button
              size="sm"
              icon={Plus}
              onClick={() => setAddingMeasurement(true)}
            >
              <Trans>Add measurement</Trans>
            </Button>
            {addingMeasurement && (
              <AddingDialog
                onConfirm={(data) => {
                  setAddingMeasurement(false);
                  model.addingList.self_measurement_config.push(data);
                }}
                onClose={() => setAddingMeasurement(false)}
              />
            )}
          </div>

          <div className="border-border border-t" />

          <FormGrid>
            <FormField
              label={<Trans>Create user</Trans>}
              tooltip={<Trans>Creates an app account for the patient</Trans>}
            >
              <Switch attr="createUser" model={model} />
            </FormField>

            {model.createUser && (
              <>
                <FormField
                  label={<Trans>Device handover</Trans>}
                  tooltip={
                    <Trans>
                      Determines the type of welcome email sent to the patient
                    </Trans>
                  }
                  mandatory
                >
                  <Radio
                    attr="welcome_email_type"
                    model={model.addingList}
                    values={deviceHandovers}
                    required
                  />
                </FormField>

                <FormField label={<Trans>Preferred language</Trans>}>
                  <Select
                    attr="preferred_language"
                    model={model.dataUser}
                    values={supportedLanguages}
                    required
                  />
                </FormField>

                <FormField
                  label={<Trans>Registration link expiry</Trans>}
                  tooltip={
                    <Trans>
                      How long the finish registration link remains valid
                    </Trans>
                  }
                >
                  <Select
                    attr="token_expiry_timedelta"
                    model={model.dataUser}
                    values={tokenExpiries}
                    required
                  />
                </FormField>
              </>
            )}
          </FormGrid>

          {hasPermission(
            app.seerlinqApi.permissions,
            PermissionBit.CLINICAL_STUDIES,
          ) && (
            <>
              <div className="border-border border-t" />
              <div>
                <div className="mb-2 flex items-center gap-1">
                  <h3 className="text-sm font-semibold">
                    <Trans>Clinical study</Trans>
                  </h3>
                  <HelperTooltip
                    content={
                      <Trans>
                        When on, the patient will be enrolled in the selected
                        study right after creation.
                      </Trans>
                    }
                  />
                </div>
                <FormGrid>
                  <FormField label={<Trans>Enroll in clinical study</Trans>}>
                    <Switch attr="enrollInStudy" model={model} />
                  </FormField>
                </FormGrid>

                {model.enrollInStudy &&
                  (model.addingList.country ? (
                    <ClinicalStudyEnrollmentFormFields
                      enrollmentModel={model.enrollment}
                      visits={model.enrollmentVisits}
                      onStudyChange={() => void model.tryFetchPrefill()}
                      onDateChange={() => void model.tryFetchPrefill()}
                      prefillLoading={model.prefillLoading}
                    />
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      <Trans>
                        Select a country above to load the visit schedule.
                      </Trans>
                    </p>
                  ))}
              </div>
            </>
          )}

          {hasMonitoringAccess(app.seerlinqApi.permissions) && (
            <>
              <div className="border-border border-t" />

              <div>
                <div className="mb-2 flex items-center gap-1">
                  <h3 className="text-sm font-semibold">
                    <Trans>Patient Assignment</Trans>
                  </h3>
                  <HelperTooltip
                    content={
                      <Trans>
                        Perform patient assignment to control who will be able
                        to see and access this patient's data. If left empty,
                        the patient will only be visible to you and admins with
                        oversight over your region.
                      </Trans>
                    }
                  />
                </div>
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 sm:*:flex-1">
                  <FormField
                    label={<Trans>Physicians</Trans>}
                    tooltip={
                      <Trans>
                        Assign a physician to this patient. All users with
                        visibility over this physician or the clinics where this
                        physician resides will also see this patient.
                      </Trans>
                    }
                  >
                    <ChooseField
                      count={
                        model.addingList["append_to_physicians"]?.length ?? 0
                      }
                      modal={PhysiciansModalComponent}
                    />
                  </FormField>

                  <FormField
                    label={<Trans>Managing users</Trans>}
                    tooltip={
                      <Trans>
                        Explicitly assign users who should see this patient but
                        do not have visibility over the assigned physician (if
                        any).
                      </Trans>
                    }
                  >
                    <ChooseField
                      count={
                        model.addingList["append_to_managing_users"]?.length ??
                        0
                      }
                      modal={ManagingUsersModal}
                    />
                  </FormField>
                </div>
              </div>
            </>
          )}

          {(hasMonitoringAccess(app.seerlinqApi.permissions) ||
            hasAdminAccess(app.seerlinqApi.permissions)) && (
            <>
              <div className="border-border border-t" />
              <FormGrid>
                <FormField
                  label={<Trans>Remember fields</Trans>}
                  tooltip={
                    <Trans>
                      When on, race, country, region, monitoring type, and
                      health insurance are saved to this browser on submit and
                      pre-filled the next time you open this form. Turn off and
                      submit once to clear.
                    </Trans>
                  }
                >
                  <Switch attr="rememberFields" model={model} />
                </FormField>
              </FormGrid>
            </>
          )}
        </div>

        <div className="mt-6">
          <Button variant="primary" type="submit">
            <Trans>Add patient</Trans>
          </Button>
        </div>
      </Form>
    </>
  );
});
